import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { functions, httpsCallable } from '@/lib/functions';
import { logAnalyticsEvent } from '@/services/analyticsLogger';

export type TemplateRating = {
  id: string;
  templateId: string;
  tripId: string;
  userId: string;
  rating: number;
  review?: string;
  createdAt?: Date;
  updatedAt?: Date;
};

type RateTemplateRequest = {
  templateId: string;
  tripId: string;
  rating: number;
  review?: string;
};

type RateTemplateResponse = {
  success: boolean;
  rating: number;
  reviewCount: number;
};

export type TemplateReviewPreview = {
  id: string;
  rating: number;
  review: string;
  updatedAt?: Date;
};

type GetTemplateReviewPreviewRequest = {
  templateId: string;
  limit?: number;
};

type GetTemplateReviewPreviewResponse = {
  reviews: Array<{
    id: string;
    rating: number;
    review: string;
    updatedAt?: string | null;
  }>;
};

const rateTemplateCallable = httpsCallable<RateTemplateRequest, RateTemplateResponse>(
  functions,
  'rateTemplate',
);

const getTemplateReviewPreviewCallable = httpsCallable<
  GetTemplateReviewPreviewRequest,
  GetTemplateReviewPreviewResponse
>(
  functions,
  'getTemplateReviewPreview',
);

const toDate = (value: unknown): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }
  if (typeof value === 'object' && typeof (value as { toDate?: unknown }).toDate === 'function') {
    try {
      return (value as { toDate: () => Date }).toDate();
    } catch {
      return undefined;
    }
  }
  return undefined;
};

const buildTemplateRatingId = (templateId: string, userId: string) => `${templateId}_${userId}`;

export async function getUserTemplateRating(
  templateId: string,
  userId: string,
): Promise<TemplateRating | null> {
  const ratingRef = doc(db, 'templateRatings', buildTemplateRatingId(templateId, userId));
  const snapshot = await getDoc(ratingRef);
  if (!snapshot.exists()) return null;
  const data = snapshot.data();
  return {
    id: snapshot.id,
    templateId: String(data.templateId ?? templateId),
    tripId: String(data.tripId ?? ''),
    userId: String(data.userId ?? userId),
    rating: Number(data.rating ?? 0),
    review: typeof data.review === 'string' ? data.review : undefined,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

export async function rateTemplate(input: {
    templateId: string;
    tripId: string;
    rating: number;
    review?: string;
    source?: 'trip_card' | 'template_detail' | 'post_completion_prompt' | 'unknown';
}): Promise<RateTemplateResponse> {
  const rating = Math.max(1, Math.min(5, Math.round(input.rating)));
  const review = input.review?.trim();
  const { data } = await rateTemplateCallable({
    templateId: input.templateId,
    tripId: input.tripId,
    rating,
    review: review && review.length > 0 ? review : undefined,
  });

  void logAnalyticsEvent('template_rated', {
    templateId: input.templateId,
    tripId: input.tripId,
    rating,
    hasReview: Boolean(review),
    source: input.source ?? 'unknown',
  });

  return data;
}

export async function getTemplateReviewPreview(
  templateId: string,
  limit = 3,
): Promise<TemplateReviewPreview[]> {
  const { data } = await getTemplateReviewPreviewCallable({ templateId, limit });
  return (data.reviews || []).map((review) => ({
    id: review.id,
    rating: Number(review.rating),
    review: review.review,
    updatedAt: toDate(review.updatedAt),
  }));
}
